import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { CalendarClock, Megaphone, Pencil, Trash2 } from "lucide-react";
import api from "../../../services/api";
import { ConfirmDialog, Dialog, EmptyState, Loading, Switch, errorText, formatDate, useToast } from "./common";

const TITLE_MAX = 120;
const BODY_MAX = 2000;

const AUDIENCES = {
  all: "Everyone",
  student: "Students",
  teacher: "Teachers",
};

const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const toDateInput = (value) => (value ? String(value).slice(0, 10) : "");

const isExpired = (a) => Boolean(a.expires_at) && toDateInput(a.expires_at) < todayISO();

const EMPTY = { title: "", body: "", audience: "all", expires_at: "" };

const AnnouncementForm = ({ initial = EMPTY, submitLabel, savingLabel, onSubmit, onCancel, titleRef, formId }) => {
  const id = useId();
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.title.trim() && form.body.trim() && form.title.length <= TITLE_MAX && form.body.length <= BODY_MAX;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        title: form.title.trim(),
        body: form.body.trim(),
        audience: form.audience,
        expires_at: form.expires_at || null,
      });
      if (!onCancel) setForm(EMPTY); // create form: reset for the next one
    } catch (err) {
      setError(errorText(err, "Couldn't save the announcement."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form id={formId} className="admin-form" onSubmit={submit} noValidate>
      <div className="eg-field">
        <label className="eg-label" htmlFor={`${id}-title`}>
          Title
        </label>
        <input
          id={`${id}-title`}
          ref={titleRef}
          className="eg-input"
          value={form.title}
          onChange={set("title")}
          maxLength={TITLE_MAX}
          placeholder="e.g. Exams timetable is now available"
        />
        <small className="admin-counter">
          {form.title.length}/{TITLE_MAX}
        </small>
      </div>
      <div className="eg-field">
        <label className="eg-label" htmlFor={`${id}-body`}>
          Message
        </label>
        <textarea
          id={`${id}-body`}
          className="eg-input admin-textarea"
          value={form.body}
          onChange={set("body")}
          maxLength={BODY_MAX}
          rows={4}
          placeholder="What do people need to know?"
        />
        <small className="admin-counter">
          {form.body.length}/{BODY_MAX}
        </small>
      </div>
      <div className="admin-form-grid">
        <div className="eg-field">
          <label className="eg-label" htmlFor={`${id}-aud`}>
            Who sees it
          </label>
          <select id={`${id}-aud`} className="eg-input" value={form.audience} onChange={set("audience")}>
            <option value="all">Everyone</option>
            <option value="student">Students only</option>
            <option value="teacher">Teachers only</option>
          </select>
        </div>
        <div className="eg-field">
          <label className="eg-label" htmlFor={`${id}-exp`}>
            Expires on (optional)
          </label>
          <input
            id={`${id}-exp`}
            type="date"
            className="eg-input"
            value={form.expires_at}
            min={todayISO()}
            onChange={set("expires_at")}
          />
          <small className="admin-field-hint">Leave empty to keep it up until you turn it off.</small>
        </div>
      </div>
      {error && <div className="admin-error admin-error-tight">{error}</div>}
      <div className="admin-form-actions">
        {onCancel && (
          <button type="button" className="eg-btn eg-btn-outline" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button type="submit" className="eg-btn eg-btn-primary" disabled={!valid || saving}>
          {saving ? savingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
};

const AnnouncementsPanel = ({ intent }) => {
  const { notify } = useToast();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [rowError, setRowError] = useState({});
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const titleRef = useRef(null);
  const formCardRef = useRef(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get("/admin/announcements");
      setItems(Array.isArray(res.data?.announcements) ? res.data.announcements : []);
    } catch (err) {
      setError(errorText(err, "Couldn't load announcements."));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (intent?.action === "new-announcement") {
      const t = setTimeout(() => {
        formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        titleRef.current?.focus({ preventScroll: true });
      }, 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [intent]);

  const create = async (body) => {
    const res = await api.post("/admin/announcements", body);
    setItems((list) => [res.data, ...(list || [])]);
    notify(`Announcement published to ${AUDIENCES[body.audience].toLowerCase()}.`);
  };

  const saveEdit = async (body) => {
    const res = await api.patch(`/admin/announcements/${editing.id}`, body);
    setItems((list) => list.map((a) => (a.id === editing.id ? { ...a, ...res.data } : a)));
    setEditing(null);
    notify("Announcement updated.");
  };

  const toggleActive = async (a, active) => {
    setTogglingId(a.id);
    setRowError((e) => ({ ...e, [a.id]: null }));
    try {
      const res = await api.patch(`/admin/announcements/${a.id}`, { active });
      setItems((list) => list.map((x) => (x.id === a.id ? { ...x, ...res.data } : x)));
      notify(active ? "Announcement is live again." : "Announcement hidden.");
    } catch (err) {
      setRowError((e) => ({ ...e, [a.id]: errorText(err, "Couldn't change this announcement.") }));
    } finally {
      setTogglingId(null);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/admin/announcements/${toDelete.id}`);
      setItems((list) => list.filter((a) => a.id !== toDelete.id));
      setToDelete(null);
      notify("Announcement deleted.");
    } catch (err) {
      setDeleteError(errorText(err, "Couldn't delete this announcement."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <section className="dashboard-section" ref={formCardRef} aria-labelledby="admin-new-ann">
        <h3 id="admin-new-ann">New announcement</h3>
        <p className="admin-field-hint admin-section-intro">
          Announcements appear as a banner at the top of student and teacher dashboards until they expire, are
          turned off, or the reader dismisses them.
        </p>
        <AnnouncementForm
          submitLabel="Publish announcement"
          savingLabel="Publishing…"
          onSubmit={create}
          titleRef={titleRef}
        />
      </section>

      <section className="dashboard-section" aria-labelledby="admin-ann-list">
        <h3 id="admin-ann-list">All announcements</h3>
        {error ? (
          <div className="admin-error admin-error-row">
            <span>{error}</span>
            <button type="button" className="eg-btn eg-btn-outline" onClick={load}>
              Try again
            </button>
          </div>
        ) : !items ? (
          <Loading label="Loading announcements…" />
        ) : items.length === 0 ? (
          <EmptyState icon={Megaphone} title="No announcements yet">
            Use the form above to post your first one.
          </EmptyState>
        ) : (
          <ul className="admin-ann-list">
            {items.map((a) => {
              const expired = isExpired(a);
              const live = a.active && !expired;
              return (
                <li key={a.id} className={`admin-ann ${live ? "" : "is-off"}`}>
                  <div className="admin-ann-top">
                    <div className="admin-badges">
                      <span className="admin-badge admin-badge-info">{AUDIENCES[a.audience] || a.audience}</span>
                      {expired ? (
                        <span className="admin-badge admin-badge-neutral">Expired</span>
                      ) : a.active ? (
                        <span className="admin-badge admin-badge-success">Live</span>
                      ) : (
                        <span className="admin-badge admin-badge-neutral">Hidden</span>
                      )}
                    </div>
                    <label className="admin-inline-switch">
                      <span>{a.active ? "Active" : "Inactive"}</span>
                      <Switch
                        checked={Boolean(a.active)}
                        onChange={(v) => toggleActive(a, v)}
                        disabled={togglingId === a.id}
                        label={`Active: ${a.title}`}
                      />
                    </label>
                  </div>
                  <h4>{a.title}</h4>
                  <p className="admin-ann-body">{a.body}</p>
                  <div className="admin-ann-foot">
                    <span className="admin-ann-meta">
                      <CalendarClock size={14} aria-hidden="true" />
                      {a.expires_at ? `${expired ? "Expired" : "Expires"} ${formatDate(a.expires_at)}` : "No expiry"}
                      {" · "}Posted {formatDate(a.created_at)}
                      {a.created_by ? ` by ${a.created_by}` : ""}
                    </span>
                    <div className="admin-row-actions">
                      <button type="button" className="eg-btn eg-btn-outline admin-btn-sm" onClick={() => setEditing(a)}>
                        <Pencil size={14} /> Edit
                      </button>
                      <button
                        type="button"
                        className="eg-btn admin-btn-danger-outline admin-btn-sm"
                        onClick={() => {
                          setDeleteError(null);
                          setToDelete(a);
                        }}
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                  {rowError[a.id] && <div className="admin-error admin-error-tight">{rowError[a.id]}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} title="Edit announcement">
        {editing && (
          <AnnouncementForm
            key={editing.id}
            initial={{
              title: editing.title || "",
              body: editing.body || "",
              audience: editing.audience || "all",
              expires_at: toDateInput(editing.expires_at),
            }}
            submitLabel="Save changes"
            savingLabel="Saving…"
            onSubmit={saveEdit}
            onCancel={() => setEditing(null)}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete this announcement?"
        confirmLabel="Delete announcement"
        busy={deleting}
        error={deleteError}
        onConfirm={doDelete}
        onCancel={() => setToDelete(null)}
      >
        <p>
          <strong>{toDelete?.title}</strong> will be removed for everyone. If you only want to hide it for now,
          turn it off instead.
        </p>
      </ConfirmDialog>
    </>
  );
};

export default AnnouncementsPanel;
